import SiteContainer from './components/SiteContainer';
import Banner from './components/Banner';
import Tabs from './components/Tabs';
import Tab from './components/Tab';
import Link from './components/Link';
import DownloadButton from './components/DownloadButton';
import { TeamMember } from './components/TeamMember';
import { TAPPForm } from './components/TAPPForm';
import Drawer from './components/Drawer';
import ContactForm from './components/ContactForm';
import StockImage from './components/StockImage';
import SideBySide from './components/SideBySide';
import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown } from '@fortawesome/free-solid-svg-icons';
import ReactGA from "react-ga4";

const GOOGLE_MEASUREMENT_ID = "G-H89Y9JNVEJ";

function App() {
  ReactGA.initialize(GOOGLE_MEASUREMENT_ID);

  ReactGA.event({
    category: "custom",
    action: "pageLoad",
  });

  const [isTiny, setIsTiny] = useState(window.innerWidth < 750);

  useEffect(() => {
    const handleResize = () => {
      setIsTiny(window.innerWidth < 1500);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    /* 
    Modify site content here using simple components
    Everything is contained within the <SiteContainer> component
    You need not modify ANYTHING ELSE outside of the <SiteContainer>
    */
    <>
    <SiteContainer>
    <Banner>The Tool for Advancing Practice Performance</Banner>
      <Tabs>
        <Tab name="Overview">
          <SideBySide>
            <StockImage index={0}/>
            <div>
              <p>The TAPP is a novel, low-burden tool with <strong>113 items</strong> designed to assess practice-level primary
    care structures and processes associated with better care and clinical outcomes.</p>
              <p>The structures and processes are related to <strong>8 domains.</strong> Click on the domains below to see sample items.</p>
            </div>
          </SideBySide>
            <Drawer title="Address social factors and encourage patient engagement">
              <SideBySide>
                <ul>
                  <li>Practice uses social history template in patient encounters (e.g., living situation,
                    family configuration)</li>
                  <li>Practice provides support to patients to determine benefit eligibility</li>
                  <li>Providers use motivational interviewing to help patients set goals</li>
                </ul>
                <StockImage index={9}/>
              </SideBySide>
            </Drawer>
            <Drawer title="Reduce clinical risk factors">
              <ul>
                <li>Practice routinely screens patients for alcohol, drug, and tobacco use</li>
                <li>Practice uses pre-visit planning data to identify patients to meet with care
                managers</li>
                <li>Practice uses registries to monitor high-risk populations</li>
              </ul>
            </Drawer>
            <Drawer title="Provide enhanced care">
              <ul>
                <li>Practice has onsite providers or staff that speak the most dominant, non-English
                language spoken by patients</li>
                <li>Practice offers option for longer visits based on patient need</li>
                <li>Practice routinely identifies and contacts patients about unmet chronic care
                needs (e.g., poorly controlled diabetes)</li>
              </ul>
            </Drawer>
            <Drawer title="Expand access to care">
              <SideBySide>
                <ul>
                    <li>On-call clinicians (MD, NP, PA) are available to communicate with patients
                    outside of office hours</li>
                    <li>Practice offers same day and/or walk-in appointments</li>
                    <li>Patient sees same provider for each visit</li>
                </ul>
                <StockImage index={6}/>
              </SideBySide>
            </Drawer>
            <Drawer title="Provide ancillary services">
              <ul>
                <li>Practice offers on-site behavioral and mental health services</li>
                <li>Practice contracts with external organizations to provide comprehensive care
                management for medically complex patients</li>
                <li>Practice has a dedicated care coordinator</li>
              </ul>
            </Drawer>
            <Drawer title="Establish care team processes and workflows">
              <SideBySide>
                <ul>
                    <li>Clinicians communicate regularly with specialists to whom they routinely refer</li>
                    <li>Practice has designated staff to manage patient panel</li>
                    <li>Practice has processes in place to effectively manage care transitions to and
                    from hospital and emergency department</li>
                </ul>
                <StockImage index={7}/>
              </SideBySide>
            </Drawer>
            <Drawer title="Use clinical information systems">
              <ul>
                  <li>Clinicians assess and track medication adherence in EHR</li>
                  <li>Practice routinely identifies and contacts patients about preventive care needs
                  (e.g., vaccines and screenings)</li>
                  <li>Clinicians use the EHR to view patient visits to other clinicians (e.g., specialists at
                    other institutions/practices)</li>
              </ul>
            </Drawer>
            <Drawer title="Use data and evidence">
              <ul>
                <li>Practice actively tracks new guidelines and incorporates them into the practice</li>
                <li>Practice sets performance goals and uses benchmarking to track quality of care</li>
                <li>Providers use a visual dashboard that displays clinical quality measures to
                manage patients with chronic disease</li>
              </ul>
            </Drawer>
            <SideBySide>
              <div>
                <p>Content validity for the TAPP was established through a comprehensive literature review, a Delphi study with health services researchers, health care leaders, and primary care providers, and a series of qualitative interviews with providers and quality-improvement staff at high-performing primary care practices. Test-retest reliability, internal consistency, and predictive validity were established in a national sample of <b>247 primary care practices</b>.</p>
                <p>This work was funded by the <i>National Center for Advancing Translational Science (1U01TR002008-01)</i>.</p>
              </div>
              <StockImage index={1}/>
            </SideBySide>
 
          <h2>Suggested applications of the TAPP</h2>
            <ul>
              <li><p>Health care system/network</p>
                <ul>
                  <li>Identify practices most in need of improvement relative to other practices within the system</li>
                  <li>Assess results of quality improvement efforts to improve primary care practice performance</li>
                </ul>
              </li>
              <li><p>Primary care practice</p>
                <ul>
                  <li>Compare performance to other practices in publications</li>
                  <li>Assess where it stands relative to other practices in a health care system/network or overall average</li>
                  <li>Assess gaps in structures and processes</li>
                </ul>
              </li>
              <li><p>Researchers/evaluators</p>
                <ul>
                  <li>Assess effectiveness of interventions</li>
                </ul>
              </li>
            </ul>

        </Tab>
        <Tab name="The Team">
          <p>The TAPP was developed by a team of researchers led by principal investigators at New York
University Grossman School of Medicine and New York University School of Global Public
Health.</p>
          
          <div className={isTiny ? "tooSmall" : ""} style={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            flexWrap: "wrap",
            alignItems: "flex-start",
            justifyContent: "center",
            verticalAlign: "center",
          }}>
            <TeamMember image={'Carolyn_Berry.png'}
            title={`Carolyn A. Berry, PhD`} 
            description={`Dr. Carolyn A. Berry is a Professor in the Department of Population Health at New York
University Grossman School of Medicine. Her current research focuses on primary care,
including the evaluation of interventions to improve primary care performance, the integration of
screening and referral programs for social determinants of health into ambulatory care settings,
the increasing role of telehealth in primary care, and development and validation of measures
related to primary care.`}
            moreLink={'https://med.nyu.edu/faculty/carolyn-berry'}/>

            <TeamMember image={'Donna_Shelley.png'} 
                      title={`Donna R. Shelley, MD, MPH`} 
                      description={`Dr. Donna R. Shelley is a tenured Professor and Vice Dean for Research in the Department of
Public Health Policy and Management and the founding Co-Director of the Global Center for
Implementation Science at the NYU School of Global Public Health. She conducts translational,
population-based, and policy-relevant research that aims to accelerate dissemination and
implementation of tobacco use treatment in safety net health care delivery systems and
implementation of tobacco control policies.`}
                      moreLink={'https://publichealth.nyu.edu/faculty/donna-shelley'}/>

            <TeamMember image={'Margaret_Paul.png'} 
                      title={`Margaret (Maggie) M. Paul, PhD`} 
                      description={`Dr. Maggie Paul is an Assistant Professor of Health Services Research at the Mayo Clinic Kern
Center for the Science of Health Care Delivery. Her expertise is in mixed methods approaches to
evaluating the implementation and outcomes of interventions across a variety of clinical settings
with a focus on primary care practice improvement.`}
                      moreLink={'https://www.mayo.edu/research/faculty/paul-maggie-m-ph-d/bio-20549208'}/>

            <TeamMember image={'Stephanie_Albert.png'} 
                      title={`Stephanie L. Albert, PhD, MPA`} 
                      description={`Dr. Stephanie L. Albert is an Assistant Professor in the Department of Population Health at NYU
Grossman School of Medicine. Her research focuses on evaluating programs and policies in a
variety of contexts (healthcare settings, schools, faith-based institutions, online) that aim to
improve the health of historically minoritized communities. She is particularly interested how
structural and environmental forces shape health with an emphasis on determinants of eating
behaviors, weight stigma, media consumption, and adolescent health and well-being.`}
                      moreLink={'https://med.nyu.edu/faculty/stephanie-l-albert'}/>

            <TeamMember image={'Saul_Blecker.png'} 
                      title={`Saul Blecker, MD, MHS`} 
                      description={`Dr. Saul Blecker is a general internal medicine physician and an Associate Professor in the
Departments of Population Health and Medicine at NYU Grossman School of Medicine. His
research focuses on improving the quality of care and outcomes for patients with chronic
diseases.`}
                      moreLink={'https://nyulangone.org/doctors/1710150982/saul-b-blecker'}/>

            <TeamMember image={'Chuck_Cleland.png'} 
                      title={`Charles (Chuck) Cleland, PhD`} 
                      description={`Dr. Chuck Cleland is an Associate Professor in the Department of Population Health at NYU
Grossman School of Medicine. His research focuses on HIV treatment and prevention, pragmatic
clinical trials, and causal mediation analyses.`}
                      moreLink={'https://med.nyu.edu/faculty/charles-cleland'}/>

            <TeamMember image={'Daphna_Harel.png'} 
                      title={`Daphna Harel, PhD`} 
                      description={`Dr. Daphna Harel is an Associate Professor of Applied Statistics and Director of A3SR MS
Program at Steinhardt at the NYU Steinhardt School of Culture, Education, and Human
Development. Dr. Harel’s research focuses on modeling challenges for data arising from self-
reported questionnaires and crowdsourcing, and requiring the use of multilevel models. Her
methodological work focuses on the creation of theoretically justified guidelines for statistical
analysis and issue of model misspecification in polytomous Item Response Theory, and the
shortening of patient reported outcome measures.`}
                      moreLink={'https://steinhardt.nyu.edu/people/daphna-harel'}/>

            <TeamMember image={'Ann_Nguyen.png'} 
                      title={`Ann M. Nguyen, PhD, MPH`} 
                      description={`Dr. Ann M. Nguyen is an Assistant Research Professor at the Rutgers Center for State Health
Policy and affiliate faculty in the Department of Family Medicine and Community Health at the
Robert Wood Johnson Medical School. Her research focuses on primary care, practice
transformation, health workforce, and integrated behavioral health.`}
                      moreLink={'https://www.cshp.rutgers.edu/people/ann-m-nguyen-phd'}/>

            <TeamMember image={'Laura_Damschroder.png'} 
                      title={`Laura Damschroder, MS, MPH`} 
                      description={`Laura Damschroder is an implementation researcher, who retired after 21 years as a full-time investigator with the Veterans Affairs Center for Clinical Management Research in Ann Arbor, Michigan. She was also an Adjunct Associate Professor at Queensland University of Technology in Brisbane, Australia. Ms. Damschroder is the lead developer of the Consolidated Framework for Implementation Research (CFIR), one of the most widely used frameworks in implementation science and that was updated in 2022. Ms. Damschroder is a visiting scholar to institutions around the world, consults as an expert on publicly funded implementation research projects, leads national training and education workshops, and mentors implementation researchers.`}
                      />

            <TeamMember image={'Bryan_Weiner.png'} 
                      title={`Bryan J. Weiner, PhD`} 
                      description={`Dr. Bryan J. Weiner is Professor in the Departments of Global Health and Health Systems and Population Health at the University of Washington. Dr. Weiner’s research focuses on the implementation of innovations and evidence-based practices in healthcare. Over the past 27 years, he has examined a wide range of innovations including quality improvement practices, care management practices, and patient safety practices; as well as evidence-based clinical practices in cancer and cardiovascular disease. His research has advanced implementation science by creating knowledge about the organizational determinants of effective implementation, developing new theories of implementation, and improving the state of measurement in the field.`}
                      moreLink={'https://globalhealth.washington.edu/faculty/bryan-weiner'}/>

            <TeamMember image={'Deb_Cohen.png'} 
                      title={`Deborah J. Cohen, PhD`} 
                      description={`Dr. Deborah J. Cohen is a Professor and Research Vice Chair in the Department of Family Medicine at Oregon Health &amp; Science University. She uses her qualitative expertise to lead mixed methods teams in examining how improvements are implemented in primary care practices, to identify what changes are made, and to compare the effectiveness of observed practice change on process and outcome measures. Dr. Cohen is a member of the National Academy of Sciences, Engineering and Medicine (NASEM). She served as a member of the NASEM committee on Transforming Health Care to Create Whole Health: Strategies to Assess, Scale, and Spread the Whole Person Approach to Health, and she currently serves on the NASEM Primary Care Standing Committee.`}
                      moreLink={'https://www.ohsu.edu/people/deborah-j-cohen-phd'}/>

            <TeamMember image={'Lynne_Nemeth.png'} 
                      title={`Lynne Nemeth, RN, PhD, FAAN`} 
                      description={`Dr. Lynne Nemeth is a Professor Emerita at the Medical University of South Carolina College of Nursing. She held dual appointments in the College of Medicine and College of Graduate Studies during her tenure at MUSC and retired in 2021. Dr. Nemeth’s research focused on translating research into practice using health information technology in primary care practices to improve quality and patient safety. She led the development of conceptual frameworks underlying the PPRNet-TRIP improvement model, and synthesized a series of practice-based research pragmatic clinical trials providing the basis for measurement of practice transformation.`}
                      moreLink={'https://profiles.musc.edu/Lynne.Nemeth/'}/>
          </div>

        </Tab>
        <Tab name="The TAPP & Scoring">
          <h2>Guidelines to Use the TAPP</h2>
          <SideBySide>
            <ul>
              <li>The TAPP should be completed by individuals who have a deep understanding of practice
                policies and general operations such as a practice manager, lead provider/physician,
                medical director, or quality improvement leader.</li>
              <li>It is acceptable to have more than one individual complete the TAPP or provide input to
                its completion.</li>
              <li>
              In a multi-specialty environment, consider only the primary care department or service when responding.
              </li>
            </ul>
            <StockImage index={2}/>
          </SideBySide>
          <DownloadButton file="Tool for Advancing Practice Performance (TAPP).pdf">Download the TAPP</DownloadButton>

          <i style={{marginLeft: "5px"}}>You can complete the TAPP online and receive the score for your practice on the "Take the TAPP" page.</i>

          <h2>Scoring the TAPP</h2>
          <SideBySide>
            
            <ul>
                <li>Recode all ‘Don’t Know/Unsure’ responses to ‘No’</li>
                <li>Question 4 ‘In this practice, how often do patients see the same PCP (other than vacation time)?’:
                    <ul>
                        <li>Recode ‘Never/Rarely’, ‘Sometimes’, and ‘Usually’ to ‘No’</li>
                        <li>Recode ‘Always’ and ‘Not Applicable/Solo PCP’ to ‘Yes’</li>
                    </ul>
                </li>
                <li>Question 8 ‘Does this practice divide patients into panels that are assigned to specific PCPs and/or care teams?’:
                    <ul>
                        <li>Recode ‘Don’t Know/Unsure’ and ‘Not Applicable/Solo PCP’ to ‘No’</li>
                    </ul>
                </li>
                <li>Question 9 ‘Does this practice have designated staff to manage patient panels?’:
                    <ul>
                        <li>Recode ‘Yes, medical assistants act as panel managers’, ‘Yes, PCPs act as panel managers’, and ‘Yes, other staff act as panel managers’ to ‘Yes’</li>
                        <li>Recode ‘No, no staff dedicated to panel management’ and ‘Not Applicable, practice does not maintain patient panels’ to ‘No’</li>
                    </ul>
                </li>
                <li>Question 11 ‘Does this practice facilitate regular communication between PCPs and care coordinators (e.g., regularly scheduled meetings, communication template)?’:
                    <ul>
                        <li>Recode ‘Don’t Know/Unsure’ and ‘Not Applicable, practice does not have care coordinators’ to ‘No’</li>
                    </ul>
                </li>
                <li>Calculate the total TAPP score by summing the number of ‘Yes’ responses to all 113 items on the TAPP</li>
            </ul>
            <div>
              <StockImage index={4}/>
              <StockImage index={5}/>
            </div>
          </SideBySide>
          <DownloadButton file="Instructions for Scoring the TAPP.pdf">Download the Scoring Instructions</DownloadButton>
        </Tab>
        <Tab name="Take the TAPP">
          <SideBySide>
            <div>
              <p>Complete the TAPP and receive the score for your practice!</p>
              <p>You can also download your completed TAPP results.</p>
              <p>NOTE: Answers and scores are <strong>not</strong> recorded or tracked by this website.</p>
              <div style={{display: "flex", width: "100%", justifyContent: "center", marginTop: "50px"}}>
                <FontAwesomeIcon icon={faArrowDown} size='xl' style={{marginRight: "10px"}}/>
                <i>Scroll down to try the interactive TAPP</i>
              </div>
            </div>
            <StockImage index={3}/>
          </SideBySide>
          <TAPPForm/>
        </Tab>
        <Tab name="Psychometric Properties" hidden={true}>
          <p>WEBDEV_PLACEHOLDER: Psychometric Properties</p>
        </Tab>
        <Tab name="References">
          {/* <h2>Publications using the TAPP</h2>
          <p><i>placeholder: will add when we have citations</i></p> */}

          <h2>Development &amp; Validation Publications</h2>
          <p>
          Kwok L Paul MM, Albert SL, Harel D, Blecker S, Weiner BJ, Damschroder L, Cohen DJ, Nguyen AM, Shelley DR, Berry CA. Development of the Tool for Advancing Practice Performance, a practice-level survey to assess primary care structures and processes. PLoS One 2025.20(9): e0315558. https://doi.org/10.1371/journal.pone.0315558
          </p>

          <h2>Work that Contributed to the TAPP</h2>
        <p>
            Albert SL, Kwok L, Shelley DR, Paul MM, Blecker S, Nguyen AM, Harel D, Cleland CM, Weiner BJ, Cohen DJ, 
            Damschroder L, Berry CA. Identifying important and feasible primary care structures and processes in the US 
            healthcare system: A modified Delphi study. BMJ Open. 2024;14:e082989. doi:10.1136/ bmjopen-2023-082989.
        </p>

        <p>
            Cohen DJ, Wyte-Lake T, Bonsu P, Albert SL, Kwok L, Paul MM, Nguyen AM, Berry CA, Shelley DR. 
            Organizational factors associated with guideline concordance of chronic disease care and management practices. 
            JABFM. 2022. <Link link="https://doi.org/10.3122/jabfm.2022.210502R2">https://doi.org/10.3122/jabfm.2022.210502R2</Link>
        </p>

        <p>
            Nguyen AM, Paul MM, Shelley DR, Albert SL, Cohen DJ, Bonsu P, Wyte-Lake T, Blecker S, Berry CA. 
            Ten common structures and processes of high-performing primary care practices. J Public Health Manag Pract. 2022. 
            <Link link="https://doi.org/10.1097/PHH.0000000000001451">10.1097/PHH.0000000000001451</Link>
        </p>

        <p>
            Albert SL, Paul MM, Nguyen AM, Shelley DR, Berry CA. A qualitative study of high-performing primary care practices 
            during the COVID-19 pandemic. BMC Family Practice. 2021. 
            <Link link="https://doi.org/10.1186/s12875-021-01589-4">https://doi.org/10.1186/s12875-021-01589-4</Link>
        </p>



            <h2>Conferences</h2>
            <p>Berry C, Cleland C, Kwok L, Harel D, Paul M, Albert SL, Blecker S, Shelley D. Developing and validating the Tool for Advancing Practice Performance, a New Measure of Primary Care. Institute for Healthcare Improvement (IHI) Forum. December 2023. Orlando, FL.</p>
            <p>Paul M, Albert S, Shelley D, Blecker S, Kwok L, Berry C. Use of concept mapping to derive a taxonomy of practice-level structures and processes to improve primary care outcomes. AcademyHealth Science of Dissemination and Implementation Annual Conference. December 2023. Arlington, VA.</p>
            <p>Berry C, Cleland C, Kwok L, Harel D, Paul M, Albert SL, Blecker S, Shelley D. Development and validation of the Tool for Advancing Practice Performance (TAPP), a new measure of primary care performance. AcademyHealth Science of Dissemination and Implementation Annual Conference. December 2023. Arlington, VA.</p>
            <p>Berry C, Cleland C, Kwok L, Harel D, Paul M, Albert SL, Shelley D. Predictive validity of the Tool for Advancing Practice Performance (TAPP), a new measure to assess primary care performance. North American Primary Care Research Group Annual Meeting. November 2023. San Francisco, CA.</p>
            <p>Berry C, Kwok L, Paul M, Cleland C, Harel D, Albert SL, Shelley D. Development, scoring, and test-retest reliability of a new tool to assess primary care practice performance. North American Primary Care Research Group Annual Meeting. November 2023. San Francisco, CA.</p>
            <p>Berry C, Cleland C, Kwok L, Harel D, Paul M, Albert SL, Shelley D. Predictive validity of Tool for Advancing Practice Performance (TAPP), a novel tool to assess primary care quality. North American Primary Care Research Group Practice-Based Research Network Conference. June 2023. Bethesda, MD.</p>
            <p>Berry C, Kwok L, Paul M, Cleland C, Harel D, Albert SL, Shelley D. Developing and scoring a tool to assess quality of primary care practice: Tool for Advancing Practice Performance (TAPP). North American Primary Care Research Group Practice-Based Research Network Conference. June 2023. Bethesda, MD.</p>
            <p>Berry C, Cleland C, Kwok L, Harel D, Paul M, Albert SL, Blecker S, Shelley D. Predictive validity of TAPP, a tool to assess quality of primary care practice. AcademyHealth Annual Research Meeting. June 2023. Seattle, WA.</p>
            <p>Berry C, Kwok L, Paul M, Cleland C, Harel D, Albert SL, Blecker S, Shelley D. Development, scoring, and test-retest reliability of a tool to assess quality of primary care practice. AcademyHealth Annual Research Meeting. June 2023. Seattle, WA.</p>
            <p>Berry C, Kwok L, Paul M, Albert SL, Blecker S, Shelley D. Development and content validation of a tool to assess quality of primary care practice. Translational Science 2023. April 2023. Washington, DC.</p>
            <p>Paul MM, Shelley D, Albert SL, Blecker S, Kwok L, Berry CA. Linking practices and outcomes: Evidence for discrete practice-level strategies to improve primary care outcomes. AcademyHealth Science of Dissemination and Implementation Annual Conference. 2021. Virtual.</p>
            <p>Berry CA, Albert SL, Paul M, Thornton R, Blecker S, Shelley D. Rigorous development and validation of a measure to assess the quality of primary care practice. North American Primary Care Research Group Practice-Based Research Network Conference. 2021. Virtual.</p>
            <p>Paul MM, Berry CA, Shelley D, Albert SL, Blecker S, A taxonomy of effective primary care structures and processes which can be implemented at the practice level. North American Primary Care Research Group Practice-Based Research Network Conference. 2021. Virtual.</p>
            <p>Albert SL, Nguyen AM, Shelley D, Blecker S, Paul M, Thornton R, Berry CA. A Delphi study to identify important and feasible structures and processes in primary care practices. North American Primary Care Research Group Practice-Based Research Network Conference. 2021. Virtual.</p>
            <p>Paul M, Albert SL, Berry CA, Blecker S, Nguyen AM, Shelley D. Linking practices and outcomes: Use of a scoping review and concept mapping to develop a taxonomy of effective primary care structures and processes. AcademyHealth Science of Dissemination and Implementation Annual Conference. 2020. Virtual.</p>
            <p>Albert SL, Nguyen AM, Shelley D, Blecker S, Paul M, Thornton R, Barry M, Friedman S, Berry CA. Using a Delphi study to identify important and feasible primary care practice structures and processes. AcademyHealth Science of Dissemination and Implementation Annual Conference. 2020. Virtual.</p>
            <p>Paul MM, Nguyen AM, Albert SL, Berry CA, Nicholson N, Blecker S, Shelley D. Framing successful practice: Developing a taxonomy of primary care structures and processes predictive of high quality and patient health outcomes. AcademyHealth Science of Dissemination and Implementation Annual Conference. 2019. Arlington, VA.</p>
        </Tab>
        <Tab name="Contact">
          <p>Please use the form below to provide feedback on the TAPP, to ask questions or contact the TAPP team.</p>
          <ContactForm/>
          </Tab>
      </Tabs>

    </SiteContainer></>
  );
}

export default App;

